import { EditorTools, EditorUtils } from "@progress/kendo-react-editor";
import { icon } from "../../config";
import {
  CustomAddColumnAfter,
  CustomAddRowAfter,
  CustomDeleteColumn,
  CustomDeleteRow,
  CustomDeleteTable,
  CustomMergeCells,
  CustomSplitCell,
  CustomTableProperties,
} from "../editorTools/EditorTableTools";
import { handleMentionTask, handleSmartChip } from "./keyDownEvents";

/**
 * Constants for table row movement operations
 * @constant {Object} TABLE_ROW_OPERATIONS
 * @author Abhishek Dhillon
 */
const TABLE_ROW_OPERATIONS = {
  MOVE_UP: "up",
  MOVE_DOWN: "down",
  MOVE_TO_TOP: "top",
  MOVE_TO_BOTTOM: "bottom",
};

/**
 * Validates if the row movement operation is possible based on current position
 * @param {string} operation - The type of movement operation from TABLE_ROW_OPERATIONS
 * @param {number} currentIndex - Current index of the row
 * @param {number} totalRows - Total number of rows in the table
 * @returns {boolean} - Returns true if the operation is valid, false otherwise
 * @author Abhishek Dhillon
 */
const isValidRowMovement = (operation, currentIndex, totalRows) => {
  switch (operation) {
    case TABLE_ROW_OPERATIONS.MOVE_UP:
    case TABLE_ROW_OPERATIONS.MOVE_TO_TOP:
      return currentIndex > 0;
    case TABLE_ROW_OPERATIONS.MOVE_DOWN:
    case TABLE_ROW_OPERATIONS.MOVE_TO_BOTTOM:
      return currentIndex < totalRows - 1;
    default:
      return false;
  }
};

/**
 * Handles the movement of a table row based on the specified operation
 * @param {string} operation - The type of movement operation from TABLE_ROW_OPERATIONS
 * @param {Object} editorRef - React ref to the Kendo Editor
 * @param {Object} state - Should include `row` (DOM row) and `table` (DOM table)
 * @author Abhishek Dhillon
 */
const handleTableRowMovement = (operation, editorRef, state) => {
  if (!editorRef?.current || !state?.table || !state?.row) return;

  const view = editorRef.current.state.view;
  const { state: editorState } = view;
  const { tr, schema } = editorState;
  const domTable = state.table;
  const domRows = Array.from(domTable.rows);
  const currentRow = state.row;
  const currentIndex = domRows.indexOf(currentRow);

  // Validate operation based on current position
  if (!isValidRowMovement(operation, currentIndex, domRows.length)) {
    return;
  }

  // Find the ProseMirror table node that corresponds to the DOM table
  let tablePos = null;
  let tableNode = null;

  // Get the position of the clicked cell to help find the correct table
  const clickedCell = currentRow.cells[0];
  const cellPos = view.posAtDOM(clickedCell, 0);

  editorState.doc.descendants((node, pos) => {
    if (node.type.name === "table") {
      // Check if this table node contains our clicked position
      if (pos <= cellPos && pos + node.nodeSize > cellPos) {
        tablePos = pos;
        tableNode = node;
        return false; // stop traversal
      }
    }
    return true;
  });

  if (tablePos === null || !tableNode) {
    console.warn("Could not find table node in ProseMirror document");
    return;
  }

  // Clone the table's rows from the ProseMirror document
  const tableRows = [...tableNode.content.content];
  const rowToMove = tableRows[currentIndex];

  // Remove the row from its current position
  tableRows.splice(currentIndex, 1);

  // Insert the row at the appropriate position based on operation
  switch (operation) {
    case TABLE_ROW_OPERATIONS.MOVE_UP:
      tableRows.splice(currentIndex - 1, 0, rowToMove);
      break;
    case TABLE_ROW_OPERATIONS.MOVE_DOWN:
      tableRows.splice(currentIndex + 1, 0, rowToMove);
      break;
    case TABLE_ROW_OPERATIONS.MOVE_TO_TOP:
      tableRows.unshift(rowToMove);
      break;
    case TABLE_ROW_OPERATIONS.MOVE_TO_BOTTOM:
      tableRows.push(rowToMove);
      break;
  }

  // Create a new table node with updated rows
  const newTableNode = schema.nodes.table.create(tableNode.attrs, tableRows);

  // Replace the table in the document
  tr.replaceWith(tablePos, tablePos + tableNode.nodeSize, newTableNode);
  view.dispatch(tr);
};

/**
 * Array of row movement menu items
 * @constant {Array<Object>} tableRowMovementItems
 * @property {string} text - Display text for the menu item
 * @property {Function} action - Function to execute when menu item is clicked
 * @property {string} id - Unique identifier for the menu item
 * @author Abhishek Dhillon
 */
export const tableRowMovementItems = [
  {
    text: "Move Rows",
    customIcon: icon.SWAP_VERT,
    subItems: [
      {
        text: "Up",
        customIcon: icon.ASCENDING,
        action: (state, setState, viewProps) => {
          const { editorRef } = viewProps;
          handleTableRowMovement(TABLE_ROW_OPERATIONS.MOVE_UP, editorRef, state);
          setState({ show: false });
        },
        id: "table-move-row-up",
      },
      {
        text: "Down",
        customIcon: icon.DESCENDING,
        action: (state, setState, viewProps) => {
          const { editorRef } = viewProps;
          handleTableRowMovement(TABLE_ROW_OPERATIONS.MOVE_DOWN, editorRef, state);
          setState({ show: false });
        },
        id: "table-move-row-down",
      },
      {
        text: "To Top",
        customIcon: icon.VERTICAL_ALIGN_TOP,
        action: (state, setState, viewProps) => {
          const { editorRef } = viewProps;
          handleTableRowMovement(TABLE_ROW_OPERATIONS.MOVE_TO_TOP, editorRef, state);
          setState({ show: false });
        },
        id: "table-move-row-top",
      },
      {
        text: "To Bottom",
        customIcon: icon.VERTICAL_ALIGN_BOTTOM,
        action: (state, setState, viewProps) => {
          const { editorRef } = viewProps;
          handleTableRowMovement(TABLE_ROW_OPERATIONS.MOVE_TO_BOTTOM, editorRef, state);
          setState({ show: false });
        },
        id: "table-move-row-bottom",
      },
    ],
  },
];

/**
 * Array of menu items for table context menu
 * @constant {Array<Object>} tableMenuGroups
 * @property {string} text - Display text for the menu group
 * @property {string} icon - Icon identifier for the menu group
 * @property {Array<Object>} subItems - Array of submenu items
 * @author Abhishek Dhillon
 */
export const tableMenuGroups = [
  {
    text: "Table Operations",
    icon: "table",
    subItems: [
      { text: "Insert Row Below", action: CustomAddRowAfter, id: "table-insert-row-after" },
      { text: "Insert Column Right", action: CustomAddColumnAfter, id: "table-insert-column-after" },
      { text: "Delete Row", action: CustomDeleteRow, id: "table-delete-row" },
      { text: "Delete Column", action: CustomDeleteColumn, id: "table-delete-column" },
      { text: "Delete Table", action: CustomDeleteTable, id: "table-delete-table" },
      { text: "Merge Cells", action: CustomMergeCells, id: "table-merge-cells" },
      { text: "Split Cell", action: CustomSplitCell, id: "table-split-cell" },
      { text: "Properties", action: EditorTools.TableProperties, id: "table-properties" },
    ],
  },
];

/**
 * Array of clipboard-related menu items
 * @constant {Array<Object>} clipboardMenuItems
 * @property {string} text - Display text for the menu item
 * @property {string} action - Action identifier for the menu item
 * @property {string} icon - Icon identifier for the menu item
 * @author Abhishek Dhillon
 */
export const clipboardMenuItems = [
  { text: "Cut", action: "cut", icon: "cut" },
  { text: "Copy", action: "copy", icon: "copy" },
  { text: "Paste", action: "paste", icon: "clipboard" },
];

/**
 * Array of smart feature menu items
 * @constant {Array<Object>} smartFeatureMenuItems
 * @property {string} text - Display text for the menu item
 * @property {string} action - Action identifier for the menu item
 * @property {string} icon - Icon identifier for the menu item
 * @author Abhishek Dhillon
 */
export const smartFeatureMenuItems = [
  { text: "Smart Chips", action: "smartChip", customIcon: icon.SMART_CHIP },
  { text: "Add Task Links", action: "mentionTask", customIcon: icon.TASK_ALT },
];

/**
 * Combined array of all default context menu items
 * @constant {Array<Object>} defaultContextMenuItems
 * @property {string} text - Display text for the menu item
 * @property {string} action - Action identifier for the menu item
 * @property {string} icon - Icon identifier for the menu item
 * @author Abhishek Dhillon
 */
export const defaultContextMenuItems = [...clipboardMenuItems, ...smartFeatureMenuItems];

/**
 * Handles table context menu display and positioning
 * @param {Object} view - Editor view instance
 * @param {Event} domEvent - DOM event object
 * @param {Object} state - Current context menu state
 * @param {Function} setState - State setter function
 * @author Abhishek Dhillon
 */
export const handleTableContextMenu = (view, domEvent, state, setState, row) => {
  const target = domEvent?.target;
  // Find the closest table cell first, then get its parent table
  const cell = target?.closest("td, th");
  const table = cell?.closest("table");

  if (!table) {
    domEvent?.preventDefault();
    return;
  }

  // Get the clicked row if not provided
  const clickedRow = row || cell?.closest("tr");

  domEvent?.preventDefault();
  setState({
    show: true,
    offset: { left: domEvent?.clientX + 2, top: domEvent?.clientY },
    table,
    view,
    isTableMenu: true,
    row: clickedRow,
    type: "",
  });
};

/**
 * Handles default context menu display and positioning
 * @param {Object} view - Editor view instance
 * @param {Event} domEvent - DOM event object
 * @param {Object} state - Current context menu state
 * @param {Function} setState - State setter function
 * @author Abhishek Dhillon
 */
export const handleDefaultContextMenu = (view, domEvent, state, setState) => {
  domEvent.preventDefault();
  setState({
    show: true,
    offset: { left: domEvent.clientX + 2, top: domEvent.clientY },
    view,
    isTableMenu: false,
    type: "",
  });
};

/**
 * Closes the context menu by updating its state
 * @param {Object} state - Current context menu state
 * @param {Function} setState - State setter function
 * @author Abhishek Dhillon
 */
export const closeContextMenu = (state, setState) => {
  setState({ ...state, show: false });
};

/**
 * Handles the cut operation in the editor
 * @param {Object} editorRef - Reference to the editor component
 * @author Abhishek Dhillon
 */
const handleCut = (editorRef) => {
  if (editorRef?.current?.state?.view) {
    const { state: editorState } = editorRef.current.state.view;
    const { selection } = editorState;
    if (!selection?.empty) {
      const node = editorState.doc.cut(selection.from, selection.to);
      const selectionText = node.textContent;
      navigator.clipboard.writeText(selectionText);
      editorRef.current.state.view.dispatch(editorState.tr.delete(selection.from, selection.to));
    }
  }
};

/**
 * Handles the copy operation in the editor
 * @param {Object} editorRef - Reference to the editor component
 * @author Abhishek Dhillon
 */
const handleCopy = (editorRef) => {
  if (editorRef?.current?.state?.view) {
    const { state: editorState } = editorRef.current.state.view;
    const { selection } = editorState;
    if (!selection?.empty) {
      const node = editorState.doc.cut(selection.from, selection.to);
      const selectionHtml = EditorUtils.getHtml({
        doc: node,
        schema: node.type.schema,
      });
      const selectionText = node.textContent;
      navigator.clipboard.writeText(selectionText);
    }
  }
};

/**
 * Handles the paste operation in the editor
 * @param {Object} editorRef - Reference to the editor component
 * @author Abhishek Dhillon
 */
const handlePaste = (editorRef) => {
  navigator.clipboard.readText().then((text) => {
    if (editorRef?.current?.state?.view) {
      const { state: editorState } = editorRef.current.state.view;
      const { selection } = editorState;
      editorRef.current.state.view.dispatch(editorState.tr.insertText(text, selection.from, selection.to));
    }
  });
};

/**
 * Handles click events for context menu items
 * @param {Object} item - Menu item that was clicked
 * @param {Object} state - Current context menu state
 * @param {Function} setState - State setter function
 * @param {Object} viewProps - Properties passed to the view
 * @author Abhishek Dhillon
 */
export const handleMenuItemClick = (item, state, setState, viewProps) => {
  const { setInputPopup, setChipTarget, setTempChipId, setActiveTab, insertNonEditable, userId, editorRef } = viewProps;

  if (typeof item.action === "string") {
    switch (item.action) {
      case "smartChip":
        setActiveTab("users");
        handleSmartChip(insertNonEditable, setInputPopup, setChipTarget, setTempChipId, userId, "@");
        closeContextMenu(state, setState);
        break;
      case "mentionTask":
        setActiveTab("taskId");
        handleSmartChip(insertNonEditable, setInputPopup, setChipTarget, setTempChipId, userId, "#");
        closeContextMenu(state, setState);
        break;
      case "cut":
        handleCut(editorRef);
        closeContextMenu(state, setState);
        break;
      case "copy":
        handleCopy(editorRef);
        closeContextMenu(state, setState);
        break;
      case "paste":
        handlePaste(editorRef);
        closeContextMenu(state, setState);
        break;
    }
  }
};
